import 'virtual:svg-icons-register';
import './style.base.scss';
import 'vite/modulepreload-polyfill';

import './src/globaljs/breakpoints.js';
import './src/globaljs/_toTop.js';
import './src/globaljs/_loadmore.js';
import './src/globaljs/_choices.js';
import './src/globaljs/_smoothscroll.js';
import './src/globaljs/_mailobfuscator.js';

type ComponentConfig = {
  name: string;
  selector: string;
  hasJs?: boolean;
  loadCss?: boolean;
  lazy?: boolean; // only JS/component init lazy
};

type ActiveComponent = ComponentConfig & {
  elements: Element[];
};

const components: ComponentConfig[] = [
  // CSS-only components
  { name: 'contactpersons', selector: '.m-contactpersons', lazy: true },
  { name: 'download', selector: '.m-downloads', lazy: true },
  { name: 'highlight-list', selector: '.m-highlight-list', lazy: true },
  { name: 'intro', selector: '.m-intro' },
  { name: 'iframe', selector: '.m-iframe' },
  { name: 'newssliderbox', selector: '.m-newssliderbox', lazy: true },
  { name: 'quote', selector: '.m-quote', lazy: true },
  { name: 'newsbox', selector: '.m-newsbox' },
  { name: 'searchresults', selector: '.m-searchresults' },
  { name: 'searchform', selector: '.m-searchform' },
  { name: 'teaser', selector: '.m-teaser', lazy: true },
  { name: 'teaser-single', selector: '.m-teaser-single', lazy: true },
  { name: 'text', selector: '.m-text' },
  { name: 'joblist', selector: '.m-joblist', lazy: true },
  { name: 'jobcontact', selector: '.m-job-contact-box', lazy: true },
  { name: 'eventbox', selector: '.m-eventbox', lazy: true },
  { name: 'event', selector: '.m-event', lazy: true },
  { name: 'job', selector: '.m-job', lazy: true },
  { name: 'table', selector: '.m-table', lazy: true },
  { name: 'benefits', selector: '.m-benefits', lazy: true },

  // Components with JS
  { name: 'iframe', selector: '.m-iframe', hasJs: true },
  { name: 'advantages', selector: '.m-advantages', hasJs: true, lazy: true },
  { name: 'anchormenu', selector: '.m-anchormenu', hasJs: true, loadCss: false },
  { name: 'header', selector: '.m-header', hasJs: true },
  { name: 'pageheader', selector: '.m-pageheader', hasJs: true, loadCss: false },
  { name: 'accordion', selector: '.m-accordion', hasJs: true, lazy: true },
  { name: 'eventlist', selector: '.m-eventlist', hasJs: true, loadCss: false, lazy: true },
  { name: 'eventteaser', selector: '.m-eventteaser', hasJs: true, lazy: true },
  { name: 'form', selector: '.m-form', hasJs: true, lazy: true },
  { name: 'gallery', selector: '.m-gallery', hasJs: true, lazy: true },
  { name: 'heroslider', selector: '.m-heroslider', hasJs: true },
  { name: 'heroslidernested', selector: '.m-heroslidernested', hasJs: true },
  { name: 'newsslider', selector: '.m-newsslider', hasJs: true, lazy: true },
  { name: 'quicklinks', selector: '.m-quicklinks', hasJs: true },
  { name: 'logoslider', selector: '.m-logoslider', hasJs: true, lazy: true },
  { name: 'memberslist', selector: '.m-memberslist', hasJs: true },
  { name: 'member', selector: '.m-member', hasJs: true },
  { name: 'numbers', selector: '.m-numbers', hasJs: true, lazy: true },
  { name: 'newslist', selector: '.m-newslist', hasJs: true, loadCss: false },
  { name: 'image', selector: '.m-image', hasJs: true, lazy: true },
  { name: 'imagetext', selector: '.m-imagetext', hasJs: true },
  { name: 'imagetextteaser', selector: '.m-imagetextteaser', hasJs: true },
  { name: 'joblistgallery', selector: '.m-joblistgallery', hasJs: true, lazy: true },
  { name: 'rc', selector: '.m-rc', hasJs: true, lazy: true },
  { name: 'socialwall', selector: '.m-socialwall', hasJs: true, lazy: true },
  { name: 'socialsharer', selector: '.m-socialsharer', hasJs: true },
  { name: 'stickybutton', selector: '.m-stickybutton', hasJs: true },
  { name: 'linklist', selector: '.m-linklist', hasJs: true, lazy: true },
  { name: 'video', selector: '.m-video', hasJs: true, lazy: true },
  { name: 'highlight', selector: '.m-highlight', hasJs: true, lazy: true },
];

const loadedComponentCss = new Set<string>();
const loadedContribCss = new Set<string>();

function queryElements(selector: string): Element[] {
  return Array.from(document.querySelectorAll(selector));
}

function loadComponentCss(name: string): Promise<unknown> {
  if (loadedComponentCss.has(name)) {
    return Promise.resolve(null);
  }

  loadedComponentCss.add(name);

  return import(`./src/components/${name}/${name}.scss`).catch((error) => {
    console.warn(`[warn] no scss found for component "${name}"`, error);
    return null;
  });
}

async function loadComponentJs(name: string, elements: Element[]): Promise<void> {
  try {
    const mod = await import(`./src/components/${name}/${name}.js`);
    const initFn = mod?.default || mod?.init || mod?.[name];

    if (typeof initFn === 'function') {
      await initFn(elements);
    }
  } catch (error) {
    console.warn(`[warn] no js found or error while loading "${name}"`, error);
  }
}

function loadContrib(key: string, loader: () => Promise<unknown>): Promise<unknown> {
  if (loadedContribCss.has(key)) {
    return Promise.resolve(null);
  }

  loadedContribCss.add(key);
  return loader();
}

function loadContribCssForComponent(component: ComponentConfig): Promise<unknown[]> {
  const imports: Promise<unknown>[] = [];

  const swiperComponents = [
    'advantages',
    'gallery',
    'heroslider',
    'logoslider',
    'newsslider',
    'newslist',
  ];

  if (swiperComponents.includes(component.name)) {
    imports.push(
      loadContrib('swiper', () => import('./src/globalstyle/_contrib/_swiper.scss')),
    );
  }

  if (component.name === 'gallery' || component.name === 'image') {
    imports.push(
      loadContrib('glightbox', () => import('./src/globalstyle/_contrib/_glightbox.scss')),
    );
  }

  if (component.name === 'memberslist' || component.name === 'member') {
    imports.push(
      loadContrib('leaflet', () => import('./src/globalstyle/_contrib/_leaflet.scss')),
    );
  }

  if (component.name === 'form' || component.name === 'memberslist' || component.name === 'eventlist') {
    imports.push(
      loadContrib('choices', () => import('./src/globalstyle/_contrib/_choices.scss')),
    );
  }

  if (component.name === 'form') {
    if (import.meta.env.DEV) {
      imports.push(
        loadContrib('gravity-forms', () =>
          import('./src/globalstyle/_contrib/_gravity-forms-dev.scss'),
        ),
      );
    } else {
      imports.push(
        loadContrib('gravity-forms', () =>
          import('./src/globalstyle/_contrib/_gravity-forms.scss'),
        ),
      );
    }
  }

  return Promise.all(imports);
}

async function loadComponentCssOnly(component: ActiveComponent): Promise<void> {
  await loadContribCssForComponent(component);

  if (component.loadCss !== false) {
    await loadComponentCss(component.name);
  }
}

async function initComponent(component: ActiveComponent): Promise<void> {
  if (component.hasJs) {
    await loadComponentJs(component.name, component.elements);
  }
}

function observeLazyComponent(component: ActiveComponent): void {
  let loaded = false;

  if (!('IntersectionObserver' in window)) {
    initComponent(component);
    return;
  }

  const observer = new IntersectionObserver(
    async (entries) => {
      const shouldLoad = entries.some((entry) => entry.isIntersecting);

      if (!shouldLoad || loaded) return;

      loaded = true;
      observer.disconnect();

      await initComponent(component);
    },
    {
      root: null,
      rootMargin: '400px 0px',
      threshold: 0,
    },
  );

  component.elements.forEach((el) => observer.observe(el));
}

async function init(): Promise<void> {
  console.log('[initialized] main.ts');

  const activeComponents: ActiveComponent[] = components
    .map((component) => ({
      ...component,
      elements: queryElements(component.selector),
    }))
    .filter((component) => component.elements.length > 0);

  const criticalComponents = activeComponents.filter((component) => !component.lazy);
  const lazyComponents = activeComponents.filter((component) => component.lazy);

  // Load all CSS for all active components immediately.
  // JS can still be lazy-loaded via IntersectionObserver.
  await Promise.all(
    activeComponents.map((component) => loadComponentCssOnly(component)),
  );

  // Initialize critical JS immediately.
  await Promise.all(
    criticalComponents.map((component) => initComponent(component)),
  );

  // Initialize lazy JS/components near viewport.
  lazyComponents.forEach((component) => {
    observeLazyComponent(component);
  });
}

if (!(window as any).__pgMainInitialized) {
  (window as any).__pgMainInitialized = true;
  console.log('[main] init once');
  init();
}